import * as React from 'react'

import { observer } from 'mobx-react'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'

import {
  WorkflowFieldRestrictionActions,
  WorkflowFieldRestrictionContext,
  WorkflowFieldRestrictionMeta,
  WorkflowFieldRestrictionState,
} from '../WorkflowFieldRestrictionContext'

interface IProps {
  selectedTypes: WorkflowType[]
  allTypes: WorkflowType[]
  onSelectionChange: (selectedTypes: string[]) => void
  children: React.ReactNode
}

@observer
class LinkedWorkflowRestrictionProvider extends React.Component<IProps> {
  private readonly actions: WorkflowFieldRestrictionActions = {
    remove: (key: string) => {
      const remaining = this.props.selectedTypes
        .map(pt => pt.type)
        .filter(t => t !== key)
      this.props.onSelectionChange(remaining)
    },
  }

  // Two display states only — specific types are selected, or any of them is
  // allowed. There is no third "none" state.
  private readonly meta: WorkflowFieldRestrictionMeta = {
    get emptyLabel() {
      return Localization.translator.anyWorkflowTypes
    },
  }

  public render() {
    const { selectedTypes, allTypes, children } = this.props

    // Deactivated types are selectable in the picker and keep their chips, so
    // both sides of this count the same universe.
    const isAllSelected =
      allTypes.length > 0 && selectedTypes.length === allTypes.length

    const state: WorkflowFieldRestrictionState = {
      items: selectedTypes.map(pt => ({
        key: pt.type,
        label: pt.name,
        isDeleted: pt.isDeleted,
        // Deleted already reads as an alert (destructive chip), so it is not
        // additionally muted — masked here so neither render site restates it.
        isInactive: pt.isInactive && !pt.isDeleted,
        iconName: pt.iconName,
      })),
      isAllSelected,
    }

    return (
      <WorkflowFieldRestrictionContext.Provider
        value={{ state, actions: this.actions, meta: this.meta }}
      >
        {children}
      </WorkflowFieldRestrictionContext.Provider>
    )
  }
}

export default LinkedWorkflowRestrictionProvider
